import style from "./LoanOffer.module.css";
import { useReducer, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
// * Components
import ContinueBtn from "../../Buttons/SlideContinueButton/ContinueButton";
import RangeInputField from "../../DataInputFields/RangeInputField/RangeInputField2/RangeInputField2";
import styleText from "../../DataInputFields/RangeInputField/RangeInputField2/RangeInputField2.module.css";
import Loader from "../../Loader/Loader";
import {
  getCustomerMonthlyObligation,
  getLocationPermission,
  isObject,
} from "../../../helpers";
// * Icons
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
// * API Handler
import { useMutation, useQuery } from "@tanstack/react-query";
// * APIs
import {
  getCRIFData,
  getCRIFData_DB,
  getCustomerOfferSettingCriteriaWise,
  addLoanOfferDetails,
  getLeadDetails,
  updateLeadData,
} from "../../../api/services/Journey";
import {
  uploadCustomerDocument,
  addCustomerDocumentKycResponse,
} from "../../../api/services/documents";
import { getSanctionLettertTemplate } from "../../../api/services/SanctionLetter";
import { getAgreementTemplate } from "../../../api/services/Agreement";
// import { getInterestRate } from "../../../api/services/MyFinDoc";
import { getRepaymentScheduleV2 } from "../../../api/services/Maxemo";

import CustomerContext from "../../../context/CustomerContext";
// * Constants
import {
  DOMAIN_NAME,
  staticLoanAmountEnabledDomains,
  staticLoanTenureEnabledDomains,
} from "../../../constants";
// * Helpers
import {
  parseCustomDate,
  removeAllCookies,
  getCookiePathFromURL,
} from "../../../helpers";
import {
  fetchDocumentVariables,
  getDateByOffset,
  getRepaymentScheduleTable,
} from "../../../utils/SanctionLetter/SanctionLetter.utils";
// * Hooks
import useDebounce from "../../../hooks/useDebounce";
// * Utilities
import { handleDocumentVariableChange } from "../../../utils/SanctionLetter/SanctionLetter.utils";
import { formatNumber } from "../../../helpers/Others/Others.helpers";

const LoanOffer = ({
  pipelineSettings,
  dispatchAgreements,
  switchToNextSlide,
  updateLastVisitSlide,
  tenure,
  loanAmt,
  handleUpdateLeadStageStatus,
  smsTemplates,
  sendStageWiseSMS,
  rejectLead,
  handleCreateTimeline,
}) => {
  const CookiePath = getCookiePathFromURL(window.location.href);
  const { apiKey, apiSecret } = useParams();

  const { customerDetails, setCustomerDetails } = useContext(CustomerContext);

  // * Agreements Template
  const initialTemplateState = {
    template: null,
    updatedTemplate: null,
    content: null,
  };
  const initialTemplatesState = {
    sanction: { ...initialTemplateState },
    loanAgreement: { ...initialTemplateState },
  };
  const templatesReducer = (state, action) => {
    switch (action.type) {
      case "SANCTION":
        return { ...state, sanction: { ...state.sanction, ...action.payload } };
      case "LOAN-AGREEMENT":
        return {
          ...state,
          loanAgreement: { ...state.loanAgreement, ...action.payload },
        };
      default:
        return state;
    }
  };
  const [templates, dispatchTemplate] = useReducer(
    templatesReducer,
    initialTemplatesState
  );

  const loanInitialDetails = {
    eligibleLoanDetails: {
      minLoanAmount: DOMAIN_NAME === "maxemocapital" ? loanAmt : null,
      maxLoanAmount: DOMAIN_NAME === "maxemocapital" ? loanAmt : null,
      loanStep: "",
      minTenure: "",
      maxTenure: "",
      tenureStep: "",
      interestRate: "",
      interestFrequency: "",
      FOIR: "",
    },
    currentLoanDetails: {
      interestRate: "",
      interestFrequency: "",
      loanAmount: "",
      loanTenure: "",
      emi: "",
      monthlyObligations: "",
      FOIR: "",
      monthlyIncome: customerDetails.monthlyIncome
        ? customerDetails.monthlyIncome
        : customerDetails?.contact?.your_net_monthly_income
        ? parseInt(customerDetails?.contact?.your_net_monthly_income)
        : "",
    },
  };
  const loanDetailsReducer = (state, action) => {
    switch (action.type) {
      case "ELIGIBLE-LOAN-DETAILS":
        return {
          ...state,
          eligibleLoanDetails: {
            ...state.eligibleLoanDetails,
            ...action.payload,
          },
        };
      case "LOAN-DETAILS":
        return {
          ...state,
          currentLoanDetails: {
            ...state.currentLoanDetails,
            ...action.payload,
          },
        };
    }
  };
  const [loanDetailsV2, dispatchLoanDetails] = useReducer(
    loanDetailsReducer,
    loanInitialDetails
  );
  const { eligibleLoanDetails, currentLoanDetails } = loanDetailsV2;

  // * Repayment Schedule Details State
  const initialDetailsState = {
    repaymentScheduleDetails: {
      scheduleAvailable: false,
      repaymentSchedule: null,
    },
    variables: {},
  };
  const reducer = (state, action) => {
    switch (action.type) {
      case "SCHEDULE-DETAILS":
        return { ...state, repaymentScheduleDetails: action.payload };
      case "AGREEMENT-VARIABLES":
        return { ...state, variables: action.payload };
      default:
        return state;
    }
  };
  const [state, dispatch] = useReducer(reducer, initialDetailsState);

  // * For MyFinDoc Only
  const updatedLoanAmount = useDebounce(currentLoanDetails.loanAmount, 400);
  useEffect(() => {
    if (updatedLoanAmount && DOMAIN_NAME === "myfindoc") {
      const data = {
        your_desired_loan_amount: updatedLoanAmount,
      };
      handleUpdateJourneyData(data);
    }
  }, [updatedLoanAmount]);
  // * MyFinDoc Only
  const updatedTenure = useDebounce(currentLoanDetails.loanTenure, 400);

  useEffect(() => {
    handleGetCustomerOfferSettingCriteriaWise(updatedTenure, updatedLoanAmount);
  }, [updatedTenure, updatedLoanAmount]);

  const getFeesVariables = (eligibleFeesTypes) => {
    let feesVariables = {};

    const feeTypes = Object.keys(eligibleFeesTypes);
    const totalFee = feeTypes.length;

    if (eligibleFeesTypes && totalFee) {
      for (let i = 0; i < totalFee; i++) {
        const feeType = feeTypes[i];
        const fee = eligibleFeesTypes[feeType];

        let feeGroupedVariables = {
          [feeType]: `Rs.${formatNumber(fee.feesChargeWithGst)}`,
          [`${feeType} In Words`]: fee.feesChargeWithGstInWords,
        };
        if (fee.feesDetails.type_of_setting === "PERCENTAGE") {
          feeGroupedVariables[`${feeType} Percentage`] = `${parseFloat(
            fee.feesDetails.percentage
          )}%`;
        }

        feesVariables = { ...feesVariables, ...feeGroupedVariables };
      }
    }

    return feesVariables;
  };
  // * V2
  const getRepaymentScheduleV2API = useMutation({
    mutationFn: (payload) => getRepaymentScheduleV2(payload),
    onSuccess: (data) => handleGetRepaymentScheduleV2Success(data),
    onError: (error) => handleGetRepaymentScheduleV2Error(error),
  });
  const handleGetRepaymentScheduleV2Success = (data) => {
    const repaymentSchedule = data?.data?.repaymentSchedule;
    const result = data?.data?.result;

    let variables = result?.extraInformation;
    const netDisbursalAmount = result?.netDisbursalDetails?.netDisbursalAmount;
    const charges = result?.netDisbursalDetails?.charges;
    // * Elibile fees are type of fees for which the customer is eligible
    const eligibleFeesTypes =
      result?.netDisbursalDetails?.upfrontActiveFeesDetails;
    // * Active fees are fees types that are configued in the Offer Settings in the system.
    // const activeFeesTypes = result?.netDisbursalDetails?.totalFeesTypesDetails;

    if (
      data?.data?.repaymentSchedule &&
      Array.isArray(data?.data?.repaymentSchedule) &&
      data?.data?.repaymentSchedule.length
    ) {
      const schedule = {
        repaymentSchedule,
        scheduleAvailable: true,
        charges: {
          totalInterest: charges?.TotalInterest,
          totalFeesCharges: charges?.totalFeesCharges,
          brokenPeriodInterest: charges?.BrokenPeriodInterest,
        },
        fees: eligibleFeesTypes,
        disbursalAmount: netDisbursalAmount,
      };

      if (variables.roiTypeWiseValue) {
        variables.interestRate = variables.roiTypeWiseValue.Yearly;
        variables.monthlyInterestRate = variables.roiTypeWiseValue.Monthly;
        variables.halfYearlyInterestRate =
          variables.roiTypeWiseValue.HalfYearly;
        variables.quaterlyInterestRate = variables.roiTypeWiseValue.Quarterly;
      }
      let dynamicVariables = {
        ...variables,
        "apr-AnnualPercentageRate": variables.apr,
      };
      const repaymentScheduleTable =
        getRepaymentScheduleTable(repaymentSchedule);
      if (repaymentScheduleTable)
        dynamicVariables.repaymentSchedule = repaymentScheduleTable;

      if (eligibleFeesTypes && isObject(eligibleFeesTypes)) {
        const feesVariables = getFeesVariables(eligibleFeesTypes);
        dynamicVariables = { ...dynamicVariables, ...feesVariables };
      }

      // * Save caluculated EMI (V1)
      dispatchLoanDetails({
        type: "LOAN-DETAILS",
        payload: { emi: variables?.EMI?.toFixed(2) },
      });

      dispatch({
        type: "SCHEDULE-DETAILS",
        payload: schedule,
      });
      dispatch({
        type: "AGREEMENT-VARIABLES",
        payload: dynamicVariables,
      });
    }
  };
  const handleGetRepaymentScheduleV2Error = (error) => {
    console.log("ERROR : ", error);
    rejectLead();
    finishJourney();
  };
  const handleGetRepaymentScheduleV2 = (
    eligibleLoanAmount,
    eligibleLoanTenure,
    interestRate,
    interestFrequency
  ) => {
    if (pipelineSettings) {
      const {
        emi_type,
        emi_cut_off_date,
        firstCollectionDate,
        emi_disbursal_holding_days,
        emi_flat_or_reducing,
      } = pipelineSettings;

      const headers = { apiKey, apiSecret };

      let loanTenure = eligibleLoanTenure,
        loanAmount = eligibleLoanAmount;

      if (staticLoanTenureEnabledDomains.includes(DOMAIN_NAME))
        loanTenure = customerDetails?.lead?.tenure_in_months;

      if (staticLoanAmountEnabledDomains.includes(DOMAIN_NAME))
        loanAmount = customerDetails?.lead?.your_desired_loan_amount;

      let data = {
        parent_setting_name: "Offer Setting",
        pipeline_id: customerDetails.pipelineID,
        lead_id: customerDetails?.leadId,
        contact_id: customerDetails.contactID,
        tenor: loanTenure,
        loan: loanAmount,
        roi: interestRate,
        // roi_type: roi.duration,
        roi_type: interestFrequency,
        emi_cut_off_date: emi_cut_off_date,
        emi_date: firstCollectionDate,
        emi_type: emi_flat_or_reducing,
        repayment_type: emi_type,
        disbursalDate:
          emi_disbursal_holding_days !== null
            ? getDateByOffset(emi_disbursal_holding_days)
            : getDateByOffset(0),
        pipeline_id: parseInt(customerDetails.pipelineID),
      };
      let payload = {
        headers,
        data,
      };
      getRepaymentScheduleV2API.mutate(payload);
    }
  };

  // * Update Lead Info
  const updateJourneyDataAPI = useMutation({
    mutationFn: (payload) => updateLeadData(payload),
    onSuccess: () => handleGetInterestRate(),
  });
  const handleUpdateJourneyData = (data) => {
    const headers = {
      apiKey,
      apiSecret,
    };
    const payload = {
      headers,
      data,
      params: { appId: customerDetails.appID },
    };
    updateJourneyDataAPI.mutate(payload);
  };

  const finishJourney = () => {
    const slidePayload = {
      slideName: "Success : Journey Completed",
      slideIndex: 38,
      slideIcon: "Success",
    };
    // * Update Journey last visit slide
    updateLastVisitSlide(slidePayload);

    // * Send Lead Details to the domain owner via email.
    // if (DOMAIN_NAME === "maxemocapital") {
    //   handleSendLeadDetailViaEmail(loanAmt);
    // } else {
    //   handleSendLeadDetailViaEmail(currentLoanDetails.loanAmount);
    // }

    // * Finish Journey and clear all the cookies.
    removeAllCookies(CookiePath);
    switchToNextSlide(38);
  };

  // * API to get Offer Settings Criteria Wise
  const getCustomerOfferSettingsCriteriaWiseAPI = useMutation({
    mutationFn: (payload) => getCustomerOfferSettingCriteriaWise(payload),
    onSuccess: (data) => handleGetCustomerOfferSettingCriteriaWiseSuccess(data),
    onError: (error) => handleGetCustomerOfferSettingCriteriaWiseError(error),
  });
  const handleGetCustomerOfferSettingCriteriaWiseSuccess = (data) => {
    const details = data?.data;
    let settings = {};

    const validateSetting = (settingType) => {
      return details[settingType] &&
        Array.isArray(details[settingType]) &&
        details[settingType]?.length
        ? true
        : false;
    };

    if (isObject(details) && Object.keys(details).length) {
      const settingTypes = Object.keys(details);

      const getSetting = (settingType) => {
        return validateSetting(settingType) ? details[settingType][0] : {};
      };

      for (let i = 0; i < settingTypes.length; i++) {
        const settingType = settingTypes[i];

        let currentSetting;
        switch (settingType) {
          case "Loan Amount":
            currentSetting = getSetting(settingType);
            settings.minLoanAmount = currentSetting.minimum_amount
              ? parseInt(currentSetting.minimum_amount)
              : 0;
            settings.maxLoanAmount = currentSetting.maximum_amount
              ? parseInt(currentSetting.maximum_amount)
              : 0;
            settings.loanStep = currentSetting.step;

            break;
          case "Loan Tenure":
            currentSetting = getSetting(settingType);
            settings.minTenure = currentSetting.minimum_amount
              ? parseInt(currentSetting.minimum_amount)
              : 0;
            settings.maxTenure = currentSetting.maximum_amount
              ? parseInt(currentSetting.maximum_amount)
              : 0;
            settings.tenureStep = currentSetting.step;
            break;
          case "Interest Rates":
            currentSetting = getSetting(settingType);
            settings.interestRate = currentSetting.percentage;
            settings.interestFrequency = currentSetting.duration;
            break;
          case "Foir":
            currentSetting = getSetting(settingType);
            settings.FOIR = currentSetting.percentage;
            break;
        }
      }

      if (currentLoanDetails.loanAmount && currentLoanDetails.loanTenure) {
        handleGetRepaymentScheduleV2(
          currentLoanDetails.loanAmount,
          currentLoanDetails.loanTenure,
          settings.interestRate,
          settings.interestFrequency
        );
        dispatchLoanDetails({
          type: "LOAN-DETAILS",
          payload: {
            loanAmount: settings.maxLoanAmount,
            interestRate: settings.interestRate,
            interestFrequency: settings.interestFrequency,
          },
        });
      } else {
        handleGetRepaymentScheduleV2(
          settings.maxLoanAmount,
          settings.maxTenure,
          settings.interestRate,
          settings.interestFrequency
        );
        dispatchLoanDetails({
          type: "LOAN-DETAILS",
          payload: {
            loanAmount: settings.maxLoanAmount,
            loanTenure: settings.maxTenure,
            interestRate: settings.interestRate,
            interestFrequency: settings.interestFrequency,
          },
        });
        dispatchLoanDetails({
          type: "ELIGIBLE-LOAN-DETAILS",
          payload: settings,
        });
      }

      // * Fetching customer CRIF details from DB (If Exist)
      if (
        currentLoanDetails.monthlyObligations === "" &&
        !getCRIFDataDBAPI.isPending
      )
        getCRIFDataDBAPI.mutate();

      return data;
    } else {
      console.log("FINISHED AT : 484");
      finishJourney();
    }
    return data;
  };

  const handleGetCustomerOfferSettingCriteriaWiseError = (error) => {
    console.log("Error : ", error);
    console.log("FINISHED AT : 490");
    finishJourney();
  };
  const handleGetCustomerOfferSettingCriteriaWise = (
    loanTenure,
    loanAmount
  ) => {
    let data = {
      parent_setting_name: "Offer Setting",
      setting_name: "Fees",
      pipeline_id: customerDetails.pipelineID,
      lead_id: customerDetails?.leadId,
      contact_id: customerDetails.contactID,
    };

    if (loanTenure) {
      if (staticLoanTenureEnabledDomains.includes(DOMAIN_NAME))
        data.loanTenure = customerDetails?.leadDetails?.tenure_in_months;
      else data.loanTenure = loanTenure;
    }
    if (loanAmount) {
      if (staticLoanAmountEnabledDomains.includes(DOMAIN_NAME))
        data.loanAmount =
          customerDetails?.leadDetails?.your_desired_loan_amount;
      else data.loanAmount = loanAmount;
    }

    const payload = {
      data,
      headers: { apiKey, apiSecret },
    };
    getCustomerOfferSettingsCriteriaWiseAPI.mutate(payload);
  };

  // * API to add generated Loan Offer Details
  const addLoanOfferDetailsAPI = useMutation({
    mutationKey: ["addLoanOfferDetails"],
    mutationFn: (Payload) => addLoanOfferDetails(Payload),
    retry: false,
  });

  // * API to add Kyc response for customer document
  const addKycResponseAPI = useMutation({
    mutationFn: (Payload) => addCustomerDocumentKycResponse(Payload),
    retry: false,
  });
  const handleAddKycResponse = (agreementDocId) => {
    const headers = {
      apiKey,
      apiSecret,
    };
    const Payload = {
      contactId: customerDetails?.contactID,
      documentId: agreementDocId,
      kycData: {
        customerName: customerDetails?.fullName,
        loanAmount: currentLoanDetails?.loanAmount,
        loanTenure: currentLoanDetails?.loanTenure,
        interest: currentLoanDetails?.interestRate,
        emi: currentLoanDetails?.emi,
        executedOn: parseCustomDate(new Date()),
      },
      headers,
    };
    addKycResponseAPI.mutate(Payload);
  };

  // * API to upload the document
  const uploadCustomerDocumetAPI = useMutation({
    mutationKey: ["uploadCustomerDocument"],
    mutationFn: (Payload) =>
      uploadCustomerDocument(Payload).then(handleDocumentUploadSuccess),
    retry: false,
  });
  const handleDocumentUploadSuccess = (data) => {
    // * Kyc response for the document(Loan Offer Agreement)
    handleAddKycResponse(data.data.id);
    // * Add generated loan offer details
    handleAddGeneratedLoanOfferDetails(data.data.id);
    return data;
  };

  // * API to fetch user's CRIF data
  const getCRIFDataAPI = useMutation({
    mutationKey: ["getCRIFData"],
    mutationFn: () =>
      getCRIFData({
        contact_id: customerDetails.contactID,
        headers: { apiKey, apiSecret },
      }).then(handleCRIFDataSuccess),
    enabled: customerDetails.contactID ? true : false,
  });
  const handleCRIFDataSuccess = (CRIF_Data) => {
    // * Calculate monthly obligation
    const customerMonthlyObligation = getCustomerMonthlyObligation(CRIF_Data);

    dispatchLoanDetails({
      type: "LOAN-DETAILS",
      payload: { monthlyObligations: customerMonthlyObligation },
    });
    return CRIF_Data;
  };

  // * API to fetch user's CRIF data from DB
  const getCRIFDataDBAPI = useMutation({
    mutationKey: ["getCRIFData_DB"],
    mutationFn: () =>
      getCRIFData_DB({
        contact_id: customerDetails.contactID,
        headers: { apiKey, apiSecret },
      })
        .then(handleCRIFDataDBSuccess)
        .catch(handleGetCRIFDataDBError),
    enabled: customerDetails.contactID ? true : false,
    retry: false,
  });
  const handleCRIFDataDBSuccess = (data) => {
    const CRIF_Data = data.data;
    if (CRIF_Data.status !== 401) {
      // * Calculate customer's monthly obligation.
      const customerMonthlyObligation = getCustomerMonthlyObligation(
        CRIF_Data.crfi_data
      );
      dispatchLoanDetails({
        type: "LOAN-DETAILS",
        payload: { monthlyObligations: customerMonthlyObligation },
      });
    }
  };
  const handleGetCRIFDataDBError = (error) => {
    // * If CRIF data does not exist in DB, then fetch the data fromo CRIF
    getCRIFDataAPI.mutate();
    return error;
  };

  // * API to fetch Sanction Letter template
  useQuery({
    queryKey: ["sanctionLetterTemplate"],
    queryFn: () =>
      getSanctionLettertTemplate({
        pipelineId: customerDetails.pipelineID,
        headers: { apiKey, apiSecret },
      }).then(handleGetSanctionLetterSuccess),
    enabled: customerDetails.pipelineID ? true : false,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const handleGetSanctionLetterSuccess = (data) => {
    if (data.data.length) {
      const sanctionLetter = data.data[0];
      dispatchTemplate({
        type: "SANCTION",
        payload: {
          content: sanctionLetter.SanctionLetterDetails[0].content,
          template: sanctionLetter.SanctionLetterDetails[0].documentText,
          updatedTemplate: sanctionLetter.SanctionLetterDetails[0].documentText,
        },
      });
    }
    return data;
  };

  // * API to fetch Agreement template
  useQuery({
    queryKey: ["agreementTemplate"],
    queryFn: () =>
      getAgreementTemplate({
        pipelineId: customerDetails.pipelineID,
        headers: { apiKey, apiSecret },
      }).then(handleGetAgreementTemplateSuccess),
    enabled: customerDetails.pipelineID ? true : false,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const handleGetAgreementTemplateSuccess = (data) => {
    if (data.data.length) {
      const agreement = data.data[0];
      dispatchTemplate({
        type: "LOAN-AGREEMENT",
        payload: {
          content: agreement.LenderAgreementDetails[0].content,
          template: agreement.LenderAgreementDetails[0].documentText,
          updatedTemplate: agreement.LenderAgreementDetails[0].documentText,
        },
      });
    }
    return data;
  };

  // * API to fetch Lead Details
  useQuery({
    queryKey: ["leadDetails"],
    queryFn: () =>
      getLeadDetails({
        pipelineId: customerDetails.pipelineID,
        leadId: customerDetails.leadId,
        headers: { apiKey, apiSecret },
      }).then(handleGetLeadDetailsSuccess),
    enabled:
      customerDetails.pipelineID && customerDetails.leadId ? true : false,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const handleGetLeadDetailsSuccess = (data) => {
    setCustomerDetails((prev) => ({
      ...prev,
      leadDetails: data.data.leadData,
    }));

    return data;
  };

  const updateLoanDetails = (key, value) => {
    let updatedValue = value;

    switch (key) {
      case "loanTenure": {
        if (value > eligibleLoanDetails.maxTenure)
          updatedValue = eligibleLoanDetails.maxTenure;
        else if (value < eligibleLoanDetails.minTenure)
          updatedValue = eligibleLoanDetails.minTenure;
        break;
      }
      case "loanAmount": {
        if (value > eligibleLoanDetails.maxLoanAmount)
          updatedValue = eligibleLoanDetails.maxLoanAmount;
        else if (value < eligibleLoanDetails.minLoanAmount)
          updatedValue = eligibleLoanDetails.minLoanAmount;
        break;
      }
    }

    dispatchLoanDetails({
      type: "LOAN-DETAILS",
      payload: { [key]: updatedValue },
    });
  };

  const generateSanctionLetterV2 = () => {
    const foundVariables = fetchDocumentVariables(
      templates.sanction.template,
      state.variables
    );

    let sanctionTemplate = templates.sanction.template;
    let variables;
    for (let i = 0; i < foundVariables.length; i++) {
      const variable = foundVariables[i];
      if (variable.value !== null) {
        const { template, updatedVariables } = handleDocumentVariableChange(
          sanctionTemplate,
          foundVariables,
          variable.value,
          variable
        );
        sanctionTemplate = template;
        variables = updatedVariables;
      }
    }

    dispatchAgreements({
      type: "SANCTION",
      payload: {
        document: sanctionTemplate,
        documentVariables: variables,
        content: templates.sanction.content,
      },
    });
  };

  const generateAgreementV2 = () => {
    const foundVariables = fetchDocumentVariables(
      templates.loanAgreement.template,
      state.variables
    );
    let agreementTemplateString = templates.loanAgreement.template;
    let variables;
    for (let i = 0; i < foundVariables.length; i++) {
      const variable = foundVariables[i];
      if (variable.value) {
        const { template, updatedVariables } = handleDocumentVariableChange(
          agreementTemplateString,
          foundVariables,
          variable.value,
          variable
        );
        agreementTemplateString = template;
        variables = updatedVariables;
      }
    }
    dispatchAgreements({
      type: "LOAN-AGREEMENT",
      payload: {
        document: agreementTemplateString,
        documentVariables: variables,
        content: templates.loanAgreement.content,
      },
    });
  };

  const handleLoanSubmitV2 = () => {
    let slidePayload = {};

    if (customerDetails.userType === "Credit Institution") {
      if (
        pipelineSettings.sanctionLetterGeneration &&
        templates.sanction.template
      ) {
        generateSanctionLetterV2();

        if (templates.loanAgreement.template) generateAgreementV2();

        const sanctionLetterId = `${customerDetails.applicationId}/SanctionLetter`;
        setCustomerDetails((prev) => ({
          ...prev,
          loanDetails: currentLoanDetails,
          sanctionLetterId,
        }));

        slidePayload.slideName = "Sanction Letter";
        // slidePayload.slideIndex = 29;
        slidePayload.slideIndex = null;
        slidePayload.slideIcon = "SanctionLetter";
      } else {
        if (templates.loanAgreement.template) generateAgreementV2();

        setCustomerDetails((prev) => ({
          ...prev,
          loanDetails: currentLoanDetails,
        }));

        slidePayload.slideName = "Loan Agreement";
        slidePayload.slideIndex = null;
        slidePayload.slideIcon = "LoanAgreement";
      }

      // * Save generated loan offer details
      handleAddGeneratedLoanOfferDetails();

      // * Update Journey last visit slide
      updateLastVisitSlide(slidePayload);

      // * Update lead stage to Approved
      let loanAmount =
        DOMAIN_NAME === "maxemocapital"
          ? loanAmt
          : currentLoanDetails.loanAmount;

      let smsTemplateId = null;
      let timelineData = {
        type: "SMS",
        message: "",
      };
      // * If domain is Finture than mark lead as Logged and send a Logged stage SMS
      if (DOMAIN_NAME === "finture") {
        handleUpdateLeadStageStatus("Logged", loanAmount);

        // * Get Logged stage SMS template
        const loggedSmsTemplate = smsTemplates.filter(
          (template) => template.stage_name === "Logged"
        );
        if (
          loggedSmsTemplate &&
          loggedSmsTemplate.length &&
          loggedSmsTemplate[0].sms_template_id
        ) {
          smsTemplateId = loggedSmsTemplate[0].sms_template_id;
          timelineData.message =
            "SMS trigged as lead status updated to Logged.";
        }
      } else {
        let approveData = {};

        if (DOMAIN_NAME === "maxemocapital") {
          approveData.tenure = `${tenure}`;
          approveData.roi_amount = `${currentLoanDetails.interestRate}`;
        } else {
          approveData.tenure = `${currentLoanDetails.loanTenure}`;
          approveData.roi_amount = `${currentLoanDetails.interestRate}`;
        }
        approveData.approved_amount = `${loanAmount}`;

        handleUpdateLeadStageStatus("Approved", loanAmount, approveData);

        // * Get Approved stage SMS template
        const approvedSmsTemplate = smsTemplates.filter(
          (template) => template.stage_name === "Approved"
        );

        if (
          approvedSmsTemplate &&
          approvedSmsTemplate.length &&
          approvedSmsTemplate[0].sms_template_id
        ) {
          smsTemplateId = approvedSmsTemplate[0].sms_template_id;
          timelineData.message =
            "SMS trigged as lead status updated to Approved.";
        }
      }

      // * Send stage wise individual SMS
      if (smsTemplateId) {
        sendStageWiseSMS(smsTemplateId);
      }

      // * Create a timeline
      if (timelineData.message) {
        handleCreateTimeline(timelineData);
      }

      if (pipelineSettings.sanctionLetterGeneration) {
        switchToNextSlide();
      } else {
        switchToNextSlide(33);
      }
      return;
    } else {
      // * Send Lead Details to the domain owner via email.
      // handleSendLeadDetailViaEmail(currentLoanDetails.loanAmount);

      console.log("FINISHED AT : 920");
      finishJourney();
    }
  };

  const handleAddGeneratedLoanOfferDetails = async () => {
    let Payload = {
      contact_id: customerDetails?.contactID,
      lead_id: customerDetails?.leadId,
      pipeline_id: customerDetails?.pipelineID,
      loan_amount:
        DOMAIN_NAME === "maxemocapital"
          ? loanAmt
          : currentLoanDetails?.loanAmount,
      loan_tenure:
        DOMAIN_NAME === "maxemocapital"
          ? tenure
          : currentLoanDetails?.loanTenure,
      interest: currentLoanDetails?.interestRate,
      interest_frequency: DOMAIN_NAME === "myfindoc" ? "Monthly" : "Yearly",
      monthly_emi: currentLoanDetails.emi,
      emi_type: pipelineSettings?.emi_type,
      roi_type: pipelineSettings?.emi_flat_or_reducing,
      emi_cut_off_date: pipelineSettings?.emi_cut_off_date,
      emi_date: pipelineSettings?.firstCollectionDate,
      loan_accept_date: new Date(),
      monthly_obligation: currentLoanDetails?.monthlyObligations,
      headers: { apiKey, apiSecret },
    };

    const location = await getLocationPermission();
    if (location) {
      Payload = { ...Payload, ...location };
    }

    Payload.monthly_income = customerDetails.monthlyIncome
      ? customerDetails.monthlyIncome
      : customerDetails?.contact?.your_net_monthly_income;

    addLoanOfferDetailsAPI.mutate(Payload);
  };

  if (
    getCRIFDataAPI.isPending ||
    getCRIFDataDBAPI.isPending ||
    getRepaymentScheduleV2API.isPending
  ) {
    return (
      <div className={style.loaderContainer}>
        <div className={style.loaderBox}>
          <Loader />
          <p>Please wait while we are generating the best offer for you.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={style.loanOfferSlide}>
      <div className={style.typographyContainer}>
        <h1>Loan Offer</h1>
        <p>Here is your loan offer with details</p>
      </div>

      <div className={style.offerDetailsContainer}>
        {DOMAIN_NAME === "maxemocapital" ? (
          <div className={style.typographyContainer}>
            <h1>Loan Amount&nbsp;&nbsp;</h1>
            <p>&#8377;{loanAmt}</p>
            <span className={styleText.valueLimitsMaximo}>
              *The interest for the loan will be deducted upfront from the loan
              amount. Please refer to the sanction letter in the next step for
              more details.
            </span>
          </div>
        ) : (
          <RangeInputField
            label="Loan Amount"
            name="loanAmount"
            id="loanAmount"
            inputPlaceholder="Loan Amount"
            required={true}
            min={eligibleLoanDetails.minLoanAmount}
            max={eligibleLoanDetails.maxLoanAmount}
            step={eligibleLoanDetails.loanStep}
            value={currentLoanDetails.loanAmount}
            handler={(loanAmount) =>
              updateLoanDetails("loanAmount", loanAmount)
            }
            inputDisabled={true}
            icon={
              <CurrencyRupeeIcon
                style={{ fontSize: "22px", color: "#2e3092" }}
              />
            }
            customerNote={`Minimum loan amount is ${eligibleLoanDetails.minLoanAmount} and maximum loan amount is ${eligibleLoanDetails.maxLoanAmount}`}
          />
        )}

        {DOMAIN_NAME === "maxemocapital" ? (
          <div className={style.typographyContainer}>
            <h1>Loan Tenure (In Month)&nbsp;&nbsp;</h1>
            <p>{tenure}</p>
          </div>
        ) : (
          <RangeInputField
            label="Tenure ( Months )"
            name="loanTenure"
            id="loanTenure"
            inputPlaceholder="Tenure"
            required={true}
            min={eligibleLoanDetails.minTenure}
            max={eligibleLoanDetails.maxTenure}
            step={eligibleLoanDetails.tenureStep}
            value={currentLoanDetails.loanTenure}
            handler={(tenure) => updateLoanDetails("loanTenure", tenure)}
            inputDisabled={true}
            customerNote={`Minimum tenure is ${eligibleLoanDetails.minTenure} Months and maximum tenure is ${eligibleLoanDetails.maxTenure} Months.`}
          />
        )}
        <div className={style.loanDetails}>
          <div
            className={style.loanContent}
            style={
              DOMAIN_NAME === "maxemocapital"
                ? { justifyContent: "center" }
                : {}
            }
          >
            {DOMAIN_NAME !== "maxemocapital" && (
              <>
                <div className={style.content}>
                  <p>Interest Per {currentLoanDetails.interestFrequency} (%)</p>
                  <p style={{ fontWeight: 500 }}>
                    {currentLoanDetails.interestRate}
                  </p>
                </div>
                <div className={style.seperator}></div>
              </>
            )}
            <div className={style.content}>
              {DOMAIN_NAME === "maxemocapital" ? (
                <p>Upfront Monthly installment (EMI)</p>
              ) : DOMAIN_NAME === "myfindoc" ? (
                <p>Estimated Equated Monthly installment (EMI)</p>
              ) : (
                <p>Equated Monthly installment (EMI)</p>
              )}

              <p style={{ fontWeight: 500 }}>
                <CurrencyRupeeIcon className={style.rupeeIcon} />
                {currentLoanDetails?.emi
                  ? Intl.NumberFormat("en-US").format(currentLoanDetails?.emi)
                  : ""}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className={style.acceptOfferBtn}>
        <ContinueBtn
          loading={uploadCustomerDocumetAPI.isPending}
          handler={handleLoanSubmitV2}
        />
      </div>
    </div>
  );
};

export default LoanOffer;
